import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { authApi } from "../../api";
import { CALENDLY_URL } from "../../config/marketing";
import { useAuth } from "../../context/AuthContext";
import { useSubmitLock } from "../../hooks/useSubmitLock";
import brandLogo from "../../logos/zoniq.png";

import "../../components/layout/appShell.css";
import "../EventOS/EventOSPage.css";
import "./AuthGatewayPage.css";

type FormErrors = {
  email?: string;
  password?: string;
  form?: string;
};

type ForgotFormErrors = {
  email?: string;
  form?: string;
};

type ResetFormErrors = {
  password?: string;
  confirmPassword?: string;
  form?: string;
};

const AuthGatewayPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const resetToken = searchParams.get("token");
  const mode = searchParams.get("mode");
  const view: "login" | "forgot" | "reset" = resetToken ? "reset" : mode === "forgot" ? "forgot" : "login";

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotErrors, setForgotErrors] = useState<ForgotFormErrors>({});
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSubmitted, setForgotSubmitted] = useState(false);

  const [resetPassword1, setResetPassword1] = useState("");
  const [resetPassword2, setResetPassword2] = useState("");
  const [resetErrors, setResetErrors] = useState<ResetFormErrors>({});
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { run: runLocked } = useSubmitLock();

  useEffect(() => {
    if (searchParams.get("mode") === "register") {
      navigate("/auth", { replace: true });
    }
  }, [navigate, searchParams]);

  const onForgotSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await runLocked(async () => {
      const normalizedEmail = forgotEmail.trim();
      if (!normalizedEmail || !/\S+@\S+\.\S+/.test(normalizedEmail)) {
        setForgotErrors({ email: "Enter a valid email address." });
        return;
      }
      setForgotErrors({});
      setForgotLoading(true);
      try {
        await authApi.forgotPassword(normalizedEmail);
        setForgotSubmitted(true);
      } catch (err) {
        setForgotErrors({
          form: err instanceof Error ? err.message : "Unable to process request right now. Please try again.",
        });
      } finally {
        setForgotLoading(false);
      }
    });
  };

  const onResetSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await runLocked(async () => {
      const nextErrors: ResetFormErrors = {};
      if (!resetPassword1 || resetPassword1.length < 8) {
        nextErrors.password = "Password must be at least 8 characters.";
      }
      if (resetPassword1 !== resetPassword2) {
        nextErrors.confirmPassword = "Passwords do not match.";
      }
      if (Object.keys(nextErrors).length > 0) {
        setResetErrors(nextErrors);
        return;
      }
      setResetErrors({});
      setResetLoading(true);
      try {
        await authApi.resetPassword(resetToken as string, resetPassword1);
        setResetSuccess(true);
      } catch (err) {
        setResetErrors({
          form: err instanceof Error ? err.message : "Unable to reset password right now. Please try again.",
        });
      } finally {
        setResetLoading(false);
      }
    });
  };

  const validateForm = (): boolean => {
    const nextErrors: FormErrors = {};
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      nextErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(normalizedEmail)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!password) {
      nextErrors.password = "Password is required.";
    } else if (password.length < 8) {
      nextErrors.password = "Password must be at least 8 characters.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await runLocked(async () => {
      if (!validateForm()) {
        return;
      }

      setLoading(true);
      setErrors({});

      try {
        const response = await authApi.login({ email: email.trim(), password });

        setSession({
          token: response.access_token,
          refreshToken: response.refresh_token,
          user: response.user,
        });
        navigate("/calendar", { replace: true });
      } catch (err) {
        setErrors({
          form:
            err instanceof Error
              ? err.message
              : "Unable to sign in right now. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <div className="auth-modern zoniq-premium-page">
      <header className="auth-modern__nav">
        <div className="auth-modern__nav-shell zoniq-glass-nav">
          <Link to="/" className="auth-modern__nav-brand">
            <span className="auth-modern__nav-mark">
              <img src={brandLogo} alt="" />
            </span>
            <span>
              <strong>Zoniq</strong>
              <em>EventOS</em>
            </span>
          </Link>
          <Link to="/" className="auth-modern__nav-home">
            ← Back to home
          </Link>
        </div>
      </header>

      <div className="auth-modern__layout">
        <aside className="auth-modern__brand zoniq-glass-panel" aria-hidden>
          <div className="auth-modern__logo-wrap">
            <img src={brandLogo} alt="" className="auth-modern__logo" />
          </div>
          <p className="auth-modern__eyebrow">Zoniq EventOS</p>
          <h2 className="auth-modern__brand-title">Run events with clarity, control, and confidence.</h2>
          <p className="auth-modern__brand-copy">
            Manage planning, execution, and reporting in one secure platform built for enterprise event teams.
          </p>
          <div className="auth-modern__feature-list">
            <span className="auth-modern__feature-pill">Real-time dashboards</span>
            <span className="auth-modern__feature-pill">Role-based access</span>
            <span className="auth-modern__feature-pill">Workflow intelligence</span>
          </div>
        </aside>

        <main className="auth-modern__panel">
          <section className="auth-modern__card zoniq-glass-panel" aria-labelledby="auth-title">
            {view === "login" ? (
              <>
                <div className="auth-modern__card-header">
                  <img src={brandLogo} alt="Zoniq logo" className="auth-modern__card-logo" />
                  <div>
                    <p className="auth-modern__kicker">Secure sign in</p>
                    <h1 id="auth-title" className="auth-modern__title">
                      Welcome back
                    </h1>
                    <p className="auth-modern__subtitle">Sign in to continue to your workspace.</p>
                  </div>
                </div>
                <form onSubmit={onSubmit} className="auth-modern__form" noValidate>
                  <div className="auth-modern__field">
                    <label htmlFor="email" className="auth-modern__label">
                      Work email
                    </label>
                    <input
                      id="email"
                      className="auth-modern__input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      autoComplete="email"
                      aria-invalid={Boolean(errors.email)}
                    />
                    {errors.email ? (
                      <p className="auth-modern__error-text" role="alert">
                        {errors.email}
                      </p>
                    ) : null}
                  </div>

                  <div className="auth-modern__field">
                    <label htmlFor="password" className="auth-modern__label">
                      Password
                    </label>
                    <div className="auth-modern__password-wrap">
                      <input
                        id="password"
                        className="auth-modern__input auth-modern__input--password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        minLength={8}
                        autoComplete="current-password"
                        aria-invalid={Boolean(errors.password)}
                      />
                      <button
                        className="auth-modern__password-toggle"
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        aria-pressed={showPassword}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                    {errors.password ? (
                      <p className="auth-modern__error-text" role="alert">
                        {errors.password}
                      </p>
                    ) : null}
                  </div>

                  <button className="auth-modern__submit" type="submit" disabled={loading}>
                    {loading ? "Please wait..." : "Continue to workspace"}
                  </button>

                  <p className="auth-modern__helper">
                    Forgot your password?{" "}
                    <Link to="/auth?mode=forgot" className="auth-modern__switch-btn auth-modern__switch-btn--link">
                      Reset it here
                    </Link>
                  </p>
                </form>

                <div className="auth-modern__switch-row">
                  <p className="auth-modern__switch-text">
                    Need access?{" "}
                    <a
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="auth-modern__switch-btn auth-modern__switch-btn--link"
                    >
                      Book a demo
                    </a>
                  </p>
                </div>

                {errors.form ? (
                  <div className="auth-modern__form-error" role="alert" aria-live="polite">
                    {errors.form}
                  </div>
                ) : null}
              </>
            ) : null}

            {view === "forgot" ? (
              <>
                <div className="auth-modern__card-header">
                  <img src={brandLogo} alt="Zoniq logo" className="auth-modern__card-logo" />
                  <div>
                    <p className="auth-modern__kicker">Password reset</p>
                    <h1 id="auth-title" className="auth-modern__title">
                      Forgot your password?
                    </h1>
                    <p className="auth-modern__subtitle">
                      Enter your work email and we&apos;ll send you a link to reset it.
                    </p>
                  </div>
                </div>

                {forgotSubmitted ? (
                  <div className="auth-modern__form">
                    <p className="auth-modern__subtitle">
                      If an account exists for that email, a password reset link has been sent. Check your inbox.
                    </p>
                    <Link to="/auth" className="auth-modern__submit" style={{ textAlign: "center", display: "block" }}>
                      Back to sign in
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={onForgotSubmit} className="auth-modern__form" noValidate>
                    <div className="auth-modern__field">
                      <label htmlFor="forgot-email" className="auth-modern__label">
                        Work email
                      </label>
                      <input
                        id="forgot-email"
                        className="auth-modern__input"
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="you@company.com"
                        autoComplete="email"
                        aria-invalid={Boolean(forgotErrors.email)}
                      />
                      {forgotErrors.email ? (
                        <p className="auth-modern__error-text" role="alert">
                          {forgotErrors.email}
                        </p>
                      ) : null}
                    </div>

                    <button className="auth-modern__submit" type="submit" disabled={forgotLoading}>
                      {forgotLoading ? "Please wait..." : "Send reset link"}
                    </button>

                    <p className="auth-modern__helper">
                      <Link to="/auth" className="auth-modern__switch-btn auth-modern__switch-btn--link">
                        Back to sign in
                      </Link>
                    </p>

                    {forgotErrors.form ? (
                      <div className="auth-modern__form-error" role="alert" aria-live="polite">
                        {forgotErrors.form}
                      </div>
                    ) : null}
                  </form>
                )}
              </>
            ) : null}

            {view === "reset" ? (
              <>
                <div className="auth-modern__card-header">
                  <img src={brandLogo} alt="Zoniq logo" className="auth-modern__card-logo" />
                  <div>
                    <p className="auth-modern__kicker">Password reset</p>
                    <h1 id="auth-title" className="auth-modern__title">
                      Choose a new password
                    </h1>
                    <p className="auth-modern__subtitle">Enter and confirm your new password below.</p>
                  </div>
                </div>

                {resetSuccess ? (
                  <div className="auth-modern__form">
                    <p className="auth-modern__subtitle">
                      Your password has been reset. You can now sign in with your new password.
                    </p>
                    <Link to="/auth" className="auth-modern__submit" style={{ textAlign: "center", display: "block" }}>
                      Back to sign in
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={onResetSubmit} className="auth-modern__form" noValidate>
                    <div className="auth-modern__field">
                      <label htmlFor="reset-password" className="auth-modern__label">
                        New password
                      </label>
                      <input
                        id="reset-password"
                        className="auth-modern__input"
                        type="password"
                        value={resetPassword1}
                        onChange={(e) => setResetPassword1(e.target.value)}
                        placeholder="Enter a new password"
                        minLength={8}
                        autoComplete="new-password"
                        aria-invalid={Boolean(resetErrors.password)}
                      />
                      {resetErrors.password ? (
                        <p className="auth-modern__error-text" role="alert">
                          {resetErrors.password}
                        </p>
                      ) : null}
                    </div>

                    <div className="auth-modern__field">
                      <label htmlFor="reset-password-confirm" className="auth-modern__label">
                        Confirm new password
                      </label>
                      <input
                        id="reset-password-confirm"
                        className="auth-modern__input"
                        type="password"
                        value={resetPassword2}
                        onChange={(e) => setResetPassword2(e.target.value)}
                        placeholder="Re-enter your new password"
                        minLength={8}
                        autoComplete="new-password"
                        aria-invalid={Boolean(resetErrors.confirmPassword)}
                      />
                      {resetErrors.confirmPassword ? (
                        <p className="auth-modern__error-text" role="alert">
                          {resetErrors.confirmPassword}
                        </p>
                      ) : null}
                    </div>

                    <button className="auth-modern__submit" type="submit" disabled={resetLoading}>
                      {resetLoading ? "Please wait..." : "Reset password"}
                    </button>

                    {resetErrors.form ? (
                      <div className="auth-modern__form-error" role="alert" aria-live="polite">
                        {resetErrors.form}
                      </div>
                    ) : null}
                  </form>
                )}
              </>
            ) : null}
          </section>
        </main>
      </div>
    </div>
  );
};

export default AuthGatewayPage;
